"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SchemaResolver = void 0;
const uuid_1 = require("uuid");
const collections_1 = require("../shared/utils/collections");
const metadata_1 = require("./metadata");
const ref_1 = require("./ref");
class SchemaResolver {
    options;
    modelUsedNames = new Set();
    modelRealRefs = new Map();
    schemas = {};
    constructor(options) {
        this.options = options;
    }
    getSchemas() {
        return this.schemas;
    }
    substituteMagicRefs(schema) {
        if (!schema || typeof schema !== 'object') {
            return schema;
        }
        else if (Array.isArray(schema)) {
            return schema.map((it) => this.substituteMagicRefs(it));
        }
        else if (isRef(schema)) {
            return this.resolveRef(schema);
        }
        else {
            const result = {};
            for (const [key, value] of Object.entries(schema)) {
                result[key] = this.substituteMagicRefs(value);
            }
            return result;
        }
    }
    resolveRef(ref) {
        if (!(0, ref_1.isMagicRef)(ref)) {
            return ref;
        }
        const modelClass = ref.$ref();
        if (!this.modelRealRefs.has(modelClass)) {
            let name = metadata_1.metadata.className.get(modelClass) || modelClass.name;
            if (this.modelUsedNames.has(name)) {
                name = `${name}-${(0, uuid_1.v4)()}`;
            }
            const ref = `${this.options.refPrefix}/${encodeURIComponent(name)}`;
            this.modelUsedNames.add(name);
            this.modelRealRefs.set(modelClass, ref);
            this.schemas[name] = this.generateSchemaForModel(modelClass);
        }
        return {
            $ref: this.modelRealRefs.get(modelClass),
        };
    }
    generateSchemaForModel(modelClass) {
        const { classSchema, properties: propsSchema, optionals, } = this.getMetadataRecursively(modelClass);
        const requiredProps = [...propsSchema.keys()].filter((prop) => !optionals.has(prop));
        const properties = {};
        for (const [name, propSchema] of propsSchema) {
            if (typeof propSchema === 'function') {
                properties[name] = (0, ref_1.ref)(propSchema);
            }
            else {
                properties[name] = propSchema;
            }
        }
        const schema = {
            // additionalProperties: false,
            ...classSchema,
            type: 'object',
            required: requiredProps.length > 0 ? requiredProps : undefined,
            properties,
        };
        return this.substituteMagicRefs(schema);
    }
    getMetadataRecursively(modelClass) {
        const parentClass = Object.getPrototypeOf(modelClass);
        const includes = metadata_1.metadata.include.get(modelClass) || new Set();
        let classSchema;
        const properties = new Map();
        const optionals = new Set();
        const parameters = new Map();
        if (parentClass && parentClass !== Object.getPrototypeOf(Object)) {
            const fromParent = this.getMetadataRecursively(parentClass);
            if (fromParent.classSchema) {
                classSchema = fromParent.classSchema;
            }
            (0, collections_1.addAllToMap)(properties, fromParent.properties);
            (0, collections_1.addAllToSet)(optionals, fromParent.optionals);
            (0, collections_1.addAllToMap)(parameters, fromParent.parameters);
        }
        for (const includedClass of includes) {
            const fromInclude = this.getMetadataRecursively(includedClass);
            if (fromInclude.classSchema) {
                classSchema = fromInclude.classSchema;
            }
            (0, collections_1.addAllToMap)(properties, fromInclude.properties);
            (0, collections_1.addAllToSet)(optionals, fromInclude.optionals);
            (0, collections_1.addAllToMap)(parameters, fromInclude.parameters);
        }
        const ownClassSchema = metadata_1.metadata.classSchema.get(modelClass);
        const ownProperties = metadata_1.metadata.schema.get(modelClass.prototype) ||
            new Map();
        const ownOptionals = metadata_1.metadata.optional.get(modelClass.prototype) || new Set();
        const ownParameters = metadata_1.metadata.parameter.get(modelClass.prototype) ||
            new Map();
        if (ownClassSchema) {
            classSchema = ownClassSchema;
        }
        (0, collections_1.addAllToMap)(properties, ownProperties);
        (0, collections_1.addAllToSet)(optionals, ownOptionals);
        (0, collections_1.addAllToMap)(parameters, ownParameters);
        return { classSchema, properties, optionals, parameters };
    }
}
exports.SchemaResolver = SchemaResolver;
function isRef(schema) {
    return schema && '$ref' in schema;
}
